interface ServiceItem {
  en: string;
  ar: string;
}

interface ServiceSectionProps {
  id: string;
  titleEn: string;
  titleAr: string;
  subtitleAr: string;
  descEn: string;
  descAr: string;
  listTitleEn: string;
  listTitleAr: string;
  items: ServiceItem[];
  image: string;
  imageAlt: string;
  reverse?: boolean;
  icon: string;
}

export default function ServiceSection({
  id,
  titleEn,
  titleAr,
  subtitleAr,
  descEn,
  descAr,
  listTitleEn,
  listTitleAr,
  items,
  image,
  imageAlt,
  reverse = false,
  icon,
}: ServiceSectionProps) {
  return (
    <section id={id} className="py-12 sm:py-16 border-b border-[#ece8e0] last:border-b-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center`}>
          {/* Image */}
          <div className={`relative overflow-hidden h-[260px] sm:h-[380px] md:h-[440px] ${reverse ? "md:order-2" : "md:order-1"}`}>
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2744]/30 to-transparent"></div>
            <div className="absolute bottom-4 left-4 bg-[#c6a864] px-3 py-1.5 flex items-center gap-2">
              <i className={`${icon} text-white text-sm`}></i>
              <span className="text-white text-xs tracking-widest uppercase font-light">{titleEn}</span>
            </div>
          </div>

          {/* Text */}
          <div className={reverse ? "md:order-1" : "md:order-2"}>
            {/* EN Title */}
            <h2
              className="text-[#1a2744] text-2xl sm:text-3xl font-light tracking-wide mb-1 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {titleEn}
            </h2>
            {/* AR Title */}
            <p
              className="text-[#1a2744] text-xl sm:text-2xl font-bold mb-1"
              style={{ fontFamily: "'Noto Naskh Arabic', serif", direction: "rtl" }}
            >
              {titleAr}
            </p>
            <p
              className="text-[#c6a864] text-sm mb-5"
              style={{ fontFamily: "'Noto Naskh Arabic', serif", direction: "rtl" }}
            >
              {subtitleAr}
            </p>

            {/* Gold divider */}
            <div className="w-12 h-0.5 bg-[#c6a864] mb-5"></div>

            {/* EN description */}
            <p className="text-gray-500 text-sm leading-relaxed mb-3">{descEn}</p>
            {/* AR description */}
            <p
              className="text-gray-400 text-sm leading-relaxed mb-6 text-right"
              style={{ fontFamily: "'Noto Naskh Arabic', serif" }}
            >
              {descAr}
            </p>

            {/* List */}
            <div className="mb-6">
              <p className="text-[#1a2744] text-xs font-semibold uppercase tracking-widest mb-1">
                {listTitleEn}
              </p>
              <p
                className="text-[#1a2744] text-sm font-semibold mb-3 text-right"
                style={{ fontFamily: "'Noto Naskh Arabic', serif" }}
              >
                {listTitleAr}
              </p>
              <ul className="space-y-2.5">
                {items.map((item, i) => (
                  <li key={i} className="flex flex-col gap-0.5 border-l-2 border-[#c6a864]/40 pl-3">
                    <span className="text-[#1a2744] text-sm">{item.en}</span>
                    <span
                      className="text-gray-400 text-xs text-right"
                      style={{ fontFamily: "'Noto Naskh Arabic', serif" }}
                    >
                      {item.ar}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href="https://api.whatsapp.com/send?phone=96892102051&text=Hello%20Blue%20Moon%20Beauty%20Salon%20%26%20Spa%2C%20I%20would%20like%20more%20information%20about%20your%20services."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer bg-[#c6a864] text-white text-xs tracking-[0.2em] uppercase px-7 py-3.5 hover:bg-[#b8965a] transition-colors duration-300"
            >
              <i className="ri-whatsapp-line text-base"></i>
              <span>Book Now</span>
              <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }} className="opacity-80">— احجزي الآن</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
